import { authenticatedFastApiAxiosInstance as api } from "@/lib/data/http";
import {
  canonicalFileSchema,
  canonicalFileWriteSchema,
  fileVersionSchema,
  type CanonicalFile,
  type FileDestination,
} from "@/lib/data/schemas/files";
import { fileChecksum } from "@/lib/utils/fileChecksum";
import {
  getFileUploadContentType,
  ensureFileNameHasExtension,
  getFileNameParts,
} from "@/lib/utils/matterFile";
import { isAxiosError } from "axios";
import { z } from "zod";

export const retrieveFile = async (id: string) =>
  canonicalFileSchema.parse((await api.get(`files/v2/${id}`)).data);
export const retrieveFiles = async (
  destination: FileDestination | { scope: "template" },
  cursor?: string,
) => {
  const { data } = await api.get("files/v2", {
    params: { ...destination, cursor, limit: 100 },
  });
  return z
    .object({
      data: canonicalFileSchema.array(),
      nextCursor: z.string().nullable(),
    })
    .parse(data);
};

// Existing tree pickers need the full hierarchy. Read bounded pages and at most
// four folders concurrently; never return a truncated tree as a complete result.
export const retrieveFileTree = async (destination: FileDestination) => {
  const files: CanonicalFile[] = [];
  const pending: FileDestination[] = [destination];
  const visited = new Set<string>();
  while (pending.length) {
    const batch = pending.splice(0, 4);
    const folders = await Promise.all(
      batch.map(async (folder) => {
        const children: CanonicalFile[] = [];
        let cursor: string | undefined;
        const cursors = new Set<string>();
        do {
          const page = await retrieveFiles(folder, cursor);
          children.push(...page.data);
          cursor = page.nextCursor ?? undefined;
          if (cursor && cursors.has(cursor))
            throw new Error(
              "Unable to finish loading this folder. Please refresh.",
            );
          if (cursor) cursors.add(cursor);
        } while (cursor);
        return children;
      }),
    );
    for (const file of folders.flat()) {
      if (visited.has(file.id)) continue;
      visited.add(file.id);
      files.push(file);
      if (file.isFolder && file.hasChildren)
        pending.push({ parentId: file.id });
    }
  }
  return files;
};

export const createFolder = async (body: {
  name: string;
  description?: string | null;
  destination: FileDestination;
}) =>
  canonicalFileWriteSchema.parse(
    (await api.post("files", { ...body, isFolder: true })).data,
  );

export class FileAlreadyExistsError extends Error {
  constructor() {
    super("A file with this name already exists");
    this.name = "FileAlreadyExistsError";
  }
}

export const uploadFile = async ({
  file,
  expectedMode,
  expectedRevision,
  onUploadReserved,
  ...body
}: {
  file: File;
  name: string;
  description?: string | null;
  destination: FileDestination;
  targetFileId?: string;
  expectedMode?: "create" | "version";
  expectedRevision?: number;
  organizeAfterProcessing?: boolean;
  intakeBatchId?: string;
  onUploadReserved?: (id: string) => void;
}) => {
  if (file.size < 1 || file.size > 5 * 1024 ** 3)
    throw new Error("Choose a file between 1 byte and 5 GiB.");
  const sha256 = await fileChecksum(file);
  const { data: intent } = await api.post<{
    fileId: string;
    mode: "create" | "version";
    uploadToken: string;
    url: string;
    headers: Record<string, string>;
    expiresAt: string;
  }>("files/uploads", {
    ...body,
    contentType: getFileUploadContentType(file) || "application/octet-stream",
    sizeBytes: file.size,
    sha256,
  });
  onUploadReserved?.(intent.fileId);
  if (expectedMode && intent.mode !== expectedMode) {
    if (expectedMode === "create") throw new FileAlreadyExistsError();
    throw new Error(
      "This file cannot receive a new version. Refresh and try again.",
    );
  }
  if (intent.mode === "version") {
    if (
      file.type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document" &&
      getFileNameParts(body.name).fileExtension.toLowerCase() !== "docx"
    )
      throw new Error(
        "Rename this file with a .docx extension before uploading a Word version, or save a copy.",
      );
    if (expectedRevision !== undefined && body.targetFileId) {
      // Catch edits made while hashing or issuing the intent. Completion fences
      // all changes after the intent, so the two checks span the whole save.
      const current = await retrieveFile(body.targetFileId);
      if (current.revision !== expectedRevision)
        throw new Error(
          "This file changed while you were editing it. Save a copy or reopen the latest version.",
        );
    }
  }
  const uploaded = await fetch(intent.url, {
    method: "PUT",
    headers: intent.headers,
    body: file,
  });
  if (!uploaded.ok) throw new Error("File upload failed. Please try again.");
  const complete = () =>
    api.post("files", { isFolder: false, uploadToken: intent.uploadToken });
  let response;
  try {
    response = await complete();
  } catch (error) {
    // A lost receipt can safely retry the exact intent, without another PUT or version.
    if (!isAxiosError(error) || (error.response && error.response.status < 500))
      throw error;
    response = await complete();
  }
  return canonicalFileWriteSchema.parse(response.data);
};

export const createOrdinaryFile = async ({
  expectedMode,
  file,
  isFolder,
  name,
  ...body
}: {
  expectedMode?: "create";
  file?: File;
  isFolder?: boolean;
  name: string;
  description?: string | null;
  destination: FileDestination;
}) => {
  if (isFolder) return createFolder({ ...body, name });
  if (!file) throw new Error("Choose a file to upload.");
  return uploadFile({
    ...body,
    expectedMode,
    description: body.description || undefined,
    file,
    name: ensureFileNameHasExtension(
      name,
      getFileNameParts(file.name).fileExtension,
    ),
  });
};

export const updateFile = async (
  id: string,
  body: {
    revision: number;
    name?: string;
    description?: string | null;
    destination?: FileDestination;
  },
) => canonicalFileSchema.parse((await api.patch(`files/${id}`, body)).data);

export const downloadFile = async (id: string) =>
  (
    await api.get<{
      url: string;
      fileName: string;
      contentType?: string | null;
      expiresAt: string;
    }>(`files/v2/${id}/download`)
  ).data;
export const retrieveFileVersions = async (id: string, cursor?: string) =>
  z
    .object({
      data: fileVersionSchema.array(),
      nextCursor: z.string().nullable(),
    })
    .parse(
      (
        await api.get(`files/v2/${id}/versions`, {
          params: { limit: 50, cursor },
        })
      ).data,
    );
export const downloadFileVersion = async (id: string, versionId: string) =>
  (
    await api.get<{ url: string; fileName: string; expiresAt: string }>(
      `files/v2/${id}/versions/${versionId}/download`,
    )
  ).data;
export const previewFileVersion = async (id: string, versionId: string) =>
  (
    await api.get<{
      url: string;
      fileName: string;
      mimeType: string;
      expiresAt: string;
    }>(`files/v2/${id}/versions/${versionId}/preview`)
  ).data;
export const restoreFileVersion = async (
  id: string,
  versionId: string,
  body: { revision: number; requestId: string },
) =>
  canonicalFileWriteSchema.parse(
    (await api.post(`files/${id}/versions/${versionId}/restore`, body)).data,
  );

export const fileDestination = (
  scope: CanonicalFile["scope"],
  parentId?: string | null,
  owningId?: string,
): FileDestination => {
  if (scope === "template")
    throw new Error("Use the Templates screen to edit templates.");
  if (parentId) return { parentId };
  if (scope === "firm" || scope === "personal") return { scope };
  if (!owningId) throw new Error("The file's owning repository is required.");
  return scope === "mass_tort"
    ? { scope, massTortId: owningId }
    : { scope, matterId: owningId };
};

export const fileOperationMessage = (error: unknown, fallback: string) => {
  if (isAxiosError<{ meta?: { userMsg?: string } }>(error))
    return error.response?.data.meta?.userMsg || fallback;
  return error instanceof Error ? error.message : fallback;
};
